import { useState } from "react";
import {
  Stack, Card, Group, Button, Text, Avatar, Divider, Badge,
  Modal, NumberInput, Alert, Loader, Center,
} from "@mantine/core";
import {
  IconLogout, IconCashRegister, IconAlertCircle, IconCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useAuth } from "../../../shared/auth";
import { PageHeader, MoneyDisplay } from "../../../shared/ui";
import {
  useMyOpenShift, useShiftXReport, useCloseShift,
} from "../../../shared/api";

/** Kasa bu saatten uzun süredir açıksa gün sonu hatırlatması göster. */
const SHIFT_REMINDER_HOURS = 14;

const ROLE_LABEL: Record<string, string> = {
  OWNER: "Sahip",
  MANAGER: "Yönetici",
  CASHIER: "Kasiyer",
  WAITER: "Garson",
};

export function ProfilePage({ onCloseShift }: { onCloseShift: () => void }) {
  const { user, role, logout } = useAuth();
  const [closeOpen, setCloseOpen] = useState(false);
  // Kasayı yalnız kasiyer/yönetici kapatır (açanla aynı yetki). Garson
  // parasal işlemle uğraşmaz → "Kasayı Kapat" + gün-sonu uyarısı görmez.
  const canCloseShift =
    role === "OWNER" || role === "MANAGER" || role === "CASHIER";
  const { data: my } = useMyOpenShift({ enabled: canCloseShift });
  const openedAt: string | undefined = (my?.shift as any)?.opened_at;
  const shiftHours = openedAt
    ? (Date.now() - new Date(openedAt).getTime()) / 3_600_000
    : 0;
  const remindClose = canCloseShift && shiftHours >= SHIFT_REMINDER_HOURS;

  return (
    <Stack gap="md">
      <PageHeader title="Profil" />

      {remindClose && (
        <Alert color="orange" icon={<IconAlertCircle size={18} />} title="Gün sonu yaklaşıyor">
          Kasa {Math.floor(shiftHours)} saattir açık. Gününüz bittiyse
          <Text span fw={700}> Kasayı Kapat (Gün Sonu)</Text>'u unutmayın.
        </Alert>
      )}

      <Card padding="lg" radius="md" withBorder>
        <Group>
          <Avatar size="xl" radius="xl" color="blue">
            {user?.username?.[0]?.toUpperCase() || "G"}
          </Avatar>
          <Stack gap={4}>
            <Text fw={700} size="lg">
              {user?.username}
            </Text>
            <Badge color="blue" variant="light">
              {(role && ROLE_LABEL[role]) || "Personel"}
            </Badge>
          </Stack>
        </Group>
      </Card>

      {canCloseShift && (
        <Button
          size="lg"
          variant="default"
          leftSection={<IconCashRegister size={20} />}
          onClick={() => setCloseOpen(true)}
        >
          Kasayı Kapat (Gün Sonu)
        </Button>
      )}

      <Button size="lg" color="red" variant="light" leftSection={<IconLogout size={20} />} onClick={logout}>
        Çıkış Yap
      </Button>

      {canCloseShift && (
        <ShiftCloseModal
          opened={closeOpen}
          onClose={() => setCloseOpen(false)}
          onClosed={() => {
            setCloseOpen(false);
            onCloseShift();
          }}
        />
      )}
    </Stack>
  );
}

/** Gün sonu kapanış: X raporu (beklenen nakit dökümü) + sayım + variance. */
function ShiftCloseModal({
  opened, onClose, onClosed,
}: {
  opened: boolean;
  onClose: () => void;
  onClosed: () => void;
}) {
  const { data: my } = useMyOpenShift({ enabled: opened });
  const shiftId = my?.shift?.id ?? null;
  const { data: x, isLoading: xLoading } = useShiftXReport(opened ? shiftId : null);
  const closeShift = useCloseShift();

  const [counted, setCounted] = useState<number>(0);
  const expectedMinor: number = x?.expected_cash_minor ?? 0;
  const countedMinor = Math.round((counted || 0) * 100);
  const varianceMinor = countedMinor - expectedMinor;

  async function handleClose() {
    if (!shiftId) return;
    try {
      await closeShift.mutateAsync({
        shiftId, counted_cash_minor: countedMinor,
      });
      notifications.show({
        message: "Kasa kapatıldı (gün sonu).",
        color: "green", icon: <IconCheck size={16} />,
      });
      onClosed();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        // Başka kullanıcı kapatmış veya çift tıklama — state'i senkronla.
        notifications.show({
          message: "Bu kasa zaten kapatılmış.",
          color: "orange",
        });
        onClosed();
      } else {
        notifications.show({
          message: e?.response?.data?.detail || "Kasa kapatılamadı.",
          color: "red",
        });
      }
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Kasayı Kapat (Gün Sonu)" centered>
      <Stack gap="md">
        {xLoading || !x ? (
          <Center h={120}><Loader /></Center>
        ) : (
          <>
            {/* "Bu rakam nereden geldi?" — beklenen nakit dökümü */}
            <Card padding="sm" radius="md" withBorder>
              <Stack gap={4}>
                <Row label="Açılış Kasası" minor={x.opening_cash_minor} />
                <Row label="Nakit Satışlar" minor={x.cash_sales_minor} />
                {x.tips_minor > 0 && <Row label="Bahşiş" minor={x.tips_minor} />}
                {x.deposits_minor > 0 && <Row label="Kasaya Eklenen" minor={x.deposits_minor} />}
                {x.cash_refunds_minor > 0 && <Row label="Nakit İadeler" minor={-x.cash_refunds_minor} />}
                {x.expenses_paid_minor > 0 && <Row label="Kasadan Gider" minor={-x.expenses_paid_minor} />}
                {x.withdrawals_minor > 0 && <Row label="Kasadan Çekilen" minor={-x.withdrawals_minor} />}
                <Divider my={4} />
                <Group justify="space-between">
                  <Text fw={700}>Beklenen Kasa</Text>
                  <MoneyDisplay minor={expectedMinor} fw={700} />
                </Group>
              </Stack>
            </Card>

            <NumberInput
              label="Sayılan Nakit (TL)"
              description="Kasadaki nakdi say ve gerçek tutarı gir"
              value={counted}
              onChange={(v) => setCounted(Number(v) || 0)}
              size="lg"
              min={0}
              decimalScale={2}
              thousandSeparator=","
            />

            {/* Fark (variance) — yeşil denk, kırmızı eksik/fazla */}
            <Alert
              color={varianceMinor === 0 ? "green" : "red"}
              icon={<IconAlertCircle size={16} />}
            >
              <Group justify="space-between">
                <Text fw={600}>
                  {varianceMinor === 0
                    ? "Kasa denk"
                    : varianceMinor < 0
                      ? "Kasada NOKSAN"
                      : "Kasada FAZLA"}
                </Text>
                <MoneyDisplay minor={varianceMinor} fw={700} />
              </Group>
            </Alert>

            <Button
              size="lg"
              color="red"
              fullWidth
              loading={closeShift.isPending}
              disabled={!shiftId}
              onClick={handleClose}
            >
              Kasayı Kapat
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function Row({ label, minor }: { label: string; minor: number }) {
  return (
    <Group justify="space-between">
      <Text c="dimmed" size="sm">{label}</Text>
      <MoneyDisplay minor={minor} size="sm" />
    </Group>
  );
}
